import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { Toast } from "../../../helpers/alert";
import useFilteredUsersByDevice from "../../../hooks/useFilteredUsersByDevice";
import { useSelector } from "react-redux";
import { publicRequest } from "../../../requestMeth";

const ReactiveAlertFormEdit = ({
  devices,
  users,
  onCancel,
  onSave,
  initialData,
  userPrivilege,
}) => {
  const intl = useIntl();
  const userToken = useSelector((state) => state.user.currentUser?.accessToken);

  const normalizeKey = useCallback((key) => {
    if (!key) return "";
    return String(key).trim().toUpperCase();
  }, []);

  const isIndexedKey = useCallback((key) => {
    return /^(T|P)\d+$/i.test(String(key || "").trim());
  }, []);

  const getNumericIndex = useCallback((key) => {
    const match = String(key || "")
      .trim()
      .match(/\d+/);

    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
  }, []);

  const orderIndexedKeys = useCallback(
    (keys) => {
      return [...keys].sort((a, b) => getNumericIndex(a) - getNumericIndex(b));
    },
    [getNumericIndex]
  );

  const getVisibleBandCodes = useCallback(
    (bands) => {
      return (bands || [])
        .map((band) => normalizeKey(band?.code || band?.name || band?.id))
        .filter(Boolean);
    },
    [normalizeKey]
  );

  const hasVisibleValue = useCallback((value) => {
    return value !== null && value !== undefined && String(value).trim() !== "";
  }, []);

  const isUsablePeriodValue = useCallback((value) => {
    if (value === true || value === false) return false;
    if (value === null || value === undefined) return false;
    if (String(value).trim() === "") return false;
    return Number(value) > 0;
  }, []);

  const resolvePeriodsByBands = useCallback(
    (rawMap, bands) => {
      const source = rawMap && typeof rawMap === "object" ? { ...rawMap } : {};

      const normalizedEntries = Object.entries(source).map(([key, value]) => [
        normalizeKey(key),
        value,
      ]);

      const visibleCodes = getVisibleBandCodes(bands);
      const result = {};

      visibleCodes.forEach((code) => {
        result[code] = "";
      });

      normalizedEntries.forEach(([key, value]) => {
        if (visibleCodes.includes(key)) {
          result[key] = value ?? "";
        }
      });

      const indexedEntries = normalizedEntries.filter(([key]) =>
        isIndexedKey(key)
      );

      const directEntries = normalizedEntries.filter(
        ([key]) => !isIndexedKey(key)
      );

      if (indexedEntries.length > 0) {
        const orderedIndexed = orderIndexedKeys(
          indexedEntries.map(([key]) => key)
        );

        orderedIndexed.forEach((indexedKey, idx) => {
          const targetBand = visibleCodes[idx];
          if (!targetBand) return;

          const value =
            source[indexedKey] ?? source[indexedKey.toLowerCase()] ?? "";

          if (result[targetBand] === "" || result[targetBand] === null) {
            result[targetBand] = value ?? "";
          }
        });
      }

      directEntries.forEach(([key, value]) => {
        if (visibleCodes.includes(key)) {
          result[key] = value ?? "";
        }
      });

      return result;
    },
    [getVisibleBandCodes, isIndexedKey, normalizeKey, orderIndexedKeys]
  );

  const translateDay = useCallback((day) => {
    const map = {
      monday: "Lunes",
      tuesday: "Martes",
      wednesday: "Miércoles",
      thursday: "Jueves",
      friday: "Viernes",
      saturday: "Sábado",
      sunday: "Domingo",
      mon: "Lunes",
      tue: "Martes",
      wed: "Miércoles",
      thu: "Jueves",
      fri: "Viernes",
      sat: "Sábado",
      sun: "Domingo",
    };

    const normalized = String(day || "").trim().toLowerCase();
    return map[normalized] || day;
  }, []);

  const formatHour = useCallback((value) => {
    if (!value) return "";
    return String(value).slice(0, 5);
  }, []);

  const formatSegment = useCallback(
    (segment) => {
      if (!segment) return "";

      const days =
        segment.days && Array.isArray(segment.days)
          ? segment.days.map(translateDay).join(", ")
          : "";

      const from = formatHour(segment.from);
      const to = formatHour(segment.to);

      if (!days && !from && !to) return "";

      if (days && from && to) return `${days}: ${from} - ${to}`;
      if (days) return days;
      if (from && to) return `${from} - ${to}`;

      return "";
    },
    [translateDay, formatHour]
  );

  const buildTooltip = useCallback(
    (band) => {
      if (!band || !Array.isArray(band.segments) || band.segments.length === 0) {
        return "";
      }

      return band.segments.map(formatSegment).join("\n");
    },
    [formatSegment]
  );

  const toEmailVal = useCallback(
    (arr) => {
      if (!Array.isArray(arr)) return [];

      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byEmail = new Map(
        (users || []).map((u) => [String(u.email || ""), u])
      );

      return arr
        .map((v) => {
          if (
            typeof v === "number" ||
            (typeof v === "string" && /^\d+$/.test(v))
          ) {
            const u = byId.get(Number(v));
            return u?.email ? { value: u.id, label: u.email } : null;
          }

          if (typeof v === "string") {
            const u = byEmail.get(v);
            return u?.email ? { value: u.id, label: u.email } : null;
          }

          return null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const toPhoneVal = useCallback(
    (arr) => {
      if (!Array.isArray(arr)) return [];

      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byPhone = new Map(
        (users || []).map((u) => [String(u.phone || ""), u])
      );

      return arr
        .map((v) => {
          if (
            typeof v === "number" ||
            (typeof v === "string" && /^\d+$/.test(v))
          ) {
            const u = byId.get(Number(v));
            return u?.phone ? { value: u.id, label: u.phone } : null;
          }

          if (typeof v === "string") {
            const u = byPhone.get(v);
            return u?.phone ? { value: u.id, label: u.phone } : null;
          }

          return null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const resolveInitialDevice = useCallback(() => {
    const dev0 = initialData?.config?.devices?.[0];
    const devId = typeof dev0 === "object" ? dev0?.device_id ?? dev0?.id : dev0;

    return devId
      ? (devices || []).find((d) => d.value === devId) || null
      : null;
  }, [initialData, devices]);

  const buildInitialPeriods = useCallback(() => {
    const rawPeriods = initialData?.config?.periods || {};
    const normalizedPeriods = {};

    Object.entries(rawPeriods).forEach(([key, value]) => {
      const normalized = normalizeKey(key);
      if (normalized) {
        normalizedPeriods[normalized] = value;
      }
    });

    return normalizedPeriods;
  }, [initialData, normalizeKey]);

  const [formData, setFormData] = useState(() => {
    const freq = initialData?.config?.frequency || {};
    const rt = freq?.realtime || {};
    const daily = freq?.daily || {};
    const weekly = freq?.weekly || {};
    const monthly = freq?.monthly || {};

    return {
      name: initialData?.name || "",
      device: resolveInitialDevice(),
      tariff: initialData?.config?.tariff || "",
      periods: buildInitialPeriods(),
      active: initialData?.active === 1 || initialData?.active === true,
      realtimeEmails: toEmailVal(rt.emails),
      realtimePhones: toPhoneVal(rt.phones),
      dailyEmails: toEmailVal(daily.emails),
      weeklyEmails: toEmailVal(weekly.emails),
      monthlyEmails: toEmailVal(monthly.emails),
      language: initialData?.config?.language || "es",
    };
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [timebands, setTimebands] = useState([]);
  const [intervalMinutes, setIntervalMinutes] = useState(null);
  const [loadingTimebands, setLoadingTimebands] = useState(false);
  const [loadingContracted, setLoadingContracted] = useState(false);

  const lastInitKeyRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const initKey = useMemo(
    () =>
      initialData?.id ??
      initialData?.uuid ??
      initialData?.name ??
      JSON.stringify({
        devices: initialData?.config?.devices,
        tariff: initialData?.config?.tariff,
        periods: initialData?.config?.periods,
        language: initialData?.config?.language,
        freq: initialData?.config?.frequency,
        active: initialData?.active,
        name: initialData?.name,
      }),
    [initialData]
  );

  const buildStateFromInitial = useCallback(() => {
    if (!initialData) return null;

    const freq = initialData?.config?.frequency || {};
    const rt = freq?.realtime || {};
    const daily = freq?.daily || {};
    const weekly = freq?.weekly || {};
    const monthly = freq?.monthly || {};

    return {
      name: initialData?.name || "",
      device: resolveInitialDevice(),
      tariff: initialData?.config?.tariff || "",
      periods: buildInitialPeriods(),
      active: initialData?.active === 1 || initialData?.active === true,
      realtimeEmails: toEmailVal(rt.emails),
      realtimePhones: toPhoneVal(rt.phones),
      dailyEmails: toEmailVal(daily.emails),
      weeklyEmails: toEmailVal(weekly.emails),
      monthlyEmails: toEmailVal(monthly.emails),
      language: initialData?.config?.language || "es",
    };
  }, [
    initialData,
    resolveInitialDevice,
    buildInitialPeriods,
    toEmailVal,
    toPhoneVal,
  ]);

  useEffect(() => {
    if (
      !initialData ||
      !(Array.isArray(users) && users.length) ||
      !(Array.isArray(devices) && devices.length)
    ) {
      return;
    }

    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    const next = buildStateFromInitial();
    if (!next) return;

    setFormData((prev) => {
      const simplify = (s) => ({
        name: s.name,
        device: s.device?.value ?? null,
        tariff: s.tariff,
        periods: s.periods,
        active: !!s.active,
        language: s.language,
        realtimeEmails: (s.realtimeEmails || []).map((x) => x.value).sort(),
        realtimePhones: (s.realtimePhones || []).map((x) => x.value).sort(),
        dailyEmails: (s.dailyEmails || []).map((x) => x.value).sort(),
        weeklyEmails: (s.weeklyEmails || []).map((x) => x.value).sort(),
        monthlyEmails: (s.monthlyEmails || []).map((x) => x.value).sort(),
      });

      return JSON.stringify(simplify(prev)) === JSON.stringify(simplify(next))
        ? prev
        : next;
    });

    lastInitKeyRef.current = initKey;
    setIsDirty(false);
  }, [initKey, initialData, users, devices, buildStateFromInitial, isDirty]);

  const hasValidPeriods = useCallback(() => {
    const values = Object.values(formData.periods || {});

    return values.some(
      (v) => v !== "" && v !== null && v !== undefined && Number(v) > 0
    );
  }, [formData.periods]);

  useEffect(() => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = "El nombre es obligatorio";
    }

    if (!formData.device) {
      nextErrors.device = "Debe seleccionar un dispositivo";
    }

    if (!String(formData.tariff || "").trim()) {
      nextErrors.tariff = "No se encontró la estructura tarifaria del dispositivo";
    }

    if (!hasValidPeriods()) {
      nextErrors.periods =
        "Debe ingresar al menos un valor válido para un período.";
    }

    setErrors(nextErrors);
    setIsValid(Object.keys(nextErrors).length === 0);
  }, [formData, hasValidPeriods]);

  useEffect(() => {
    const deviceId = formData.device?.value;

    if (!deviceId) {
      setTimebands([]);
      setIntervalMinutes(null);
      return;
    }

    const fetchData = async () => {
      setLoadingTimebands(true);
      setLoadingContracted(true);

      try {

        const [timebandsRes, contractedRes] = await Promise.allSettled([
          publicRequest.get(`/maximeter/config-for-device`, {
            headers: { token: `Bearer ${userToken}` },
            params: { deviceId },
          }),
          publicRequest.get(`/maximeter/contracted-power`, {
            headers: { token: `Bearer ${userToken}` },
            params: { deviceId },
          }),
        ]);

        const timebandsData =
          timebandsRes.status === "fulfilled"
            ? timebandsRes.value?.data || {}
            : {};

        const contractedData =
          contractedRes.status === "fulfilled"
            ? contractedRes.value?.data || {}
            : {};

        const nextTimebands = timebandsData?.timebands || [];
        const nextInterval = timebandsData?.interval_minutes ?? null;

        const nextTariff =
          timebandsData?.tariff_structure ??
          timebandsData?.tariff ??
          timebandsData?.tariff_name ??
          "";


        const contractedMap = contractedData?.contracted_power_map || {};

        setTimebands(nextTimebands);
        setIntervalMinutes(nextInterval);

        setFormData((prev) => {
          const savedPeriods = resolvePeriodsByBands(
            prev.periods || {},
            nextTimebands
          );

          const fallbackPeriods = resolvePeriodsByBands(
            contractedMap,
            nextTimebands
          );

          const mergedPeriods = {};
          const visibleCodes = getVisibleBandCodes(nextTimebands);

          visibleCodes.forEach((code) => {
            const savedValue = savedPeriods?.[code];
            const fallbackValue = fallbackPeriods?.[code];

            mergedPeriods[code] = isUsablePeriodValue(savedValue)
              ? savedValue
              : fallbackValue ?? "";
          });

          const nextState = {
            ...prev,
            periods: mergedPeriods,
            tariff: nextTariff || prev.tariff || "",
          };

          return nextState;
        });
      } catch (error) {
        console.error("Error fetching reactive timebands:", error);
        setTimebands([]);
        setIntervalMinutes(null);
        setFormData((prev) => ({
          ...prev,
          periods: {},
          tariff: prev.tariff || "",
        }));
      } finally {
        setLoadingTimebands(false);
        setLoadingContracted(false);
      }
    };

    fetchData();
  }, [
    formData.device,
    userToken,
    resolvePeriodsByBands,
    getVisibleBandCodes,
    isUsablePeriodValue,
  ]);

  const filteredUsers = useFilteredUsersByDevice(
    formData.device ? [formData.device] : [],
    users,
    userPrivilege
  );

  const emailOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u?.email)
        .map((u) => ({ value: u.id, label: u.email })),
    [filteredUsers]
  );

  const visibleBandCodes = useMemo(() => {
    const allCodes = getVisibleBandCodes(timebands);

    return allCodes.filter((code) => hasVisibleValue(formData.periods?.[code]));
  }, [timebands, getVisibleBandCodes, formData.periods, hasVisibleValue]);

  const handleChange = (field, value) => {
    setIsDirty(true);

    if (field === "device") {
      setFormData((prev) => ({
        ...prev,
        device: value,
        tariff: "",
        periods: {},
        realtimeEmails: [],
        realtimePhones: [],
        dailyEmails: [],
        weeklyEmails: [],
        monthlyEmails: [],
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePeriodChange = (period, value) => {
    const normalized = normalizeKey(period);

    setIsDirty(true);

    setFormData((prev) => ({
      ...prev,
      periods: {
        ...prev.periods,
        [normalized]: value,
      },
    }));
  };

  const timebandInputs = useMemo(() => {
    if (!formData.device) return null;

    if (loadingTimebands || loadingContracted) {
      return <p>Cargando valores de reactiva...</p>;
    }

    if (!visibleBandCodes.length) {
      return (
        <div className="reactiva-warning">
          No se encontró configuración de bandas horarias para el dispositivo
          seleccionado.
        </div>
      );
    }

    return visibleBandCodes.map((code) => {
      const band = (timebands || []).find(
        (item) => normalizeKey(item?.code || item?.name || item?.id) === code
      );

      const tooltip = buildTooltip(band);

      return (
        <div key={code} className="maximetro-timeband-row">
          <label className="maximetro-timeband-label">
            <span>{code}</span>

            {tooltip ? (
              <span
                className="maximetro-timeband-info"
                title={tooltip}
                aria-label={`Información de la banda ${code}`}
              >
                i
              </span>
            ) : null}
          </label>

          <input
            type="number"
            className="maximetro-input small"
            placeholder="Valor"
            value={formData.periods?.[code] ?? ""}
            onChange={(e) => handlePeriodChange(code, e.target.value)}
          />
        </div>
      );
    });
  }, [
    formData.device,
    formData.periods,
    loadingTimebands,
    loadingContracted,
    visibleBandCodes,
    timebands,
    normalizeKey,
    buildTooltip,
  ]);

  const handleSubmit = () => {
    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.formatMessage({
        id: "confirm.yes",
        defaultMessage: "Sí, continuar.",
      }),
      cancelButtonText: intl.formatMessage({
        id: "confirm.no",
        defaultMessage: "Cancelar",
      }),
    }).then((result) => {
      if (result.isConfirmed && isValid) {
        const ids = (xs) => (xs || []).map((x) => x.value);

        const payload = {
          type: "REACTIVA",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            devices: formData.device?.value ? [formData.device.value] : [],
            tariff: formData.tariff,
            periods: { ...formData.periods },
            language: formData.language,
            frequency: {
              realtime: {
                enabled:
                  (formData.realtimeEmails?.length || 0) > 0 ||
                  (formData.realtimePhones?.length || 0) > 0,
                rateLimit: null,
                emails: ids(formData.realtimeEmails),
                phones: ids(formData.realtimePhones),
              },
              daily: {
                enabled: (formData.dailyEmails?.length || 0) > 0,
                time: "09:00",
                emails: ids(formData.dailyEmails),
                phones: [],
              },
              weekly: {
                enabled: (formData.weeklyEmails?.length || 0) > 0,
                day: "monday",
                time: "09:00",
                emails: ids(formData.weeklyEmails),
                phones: [],
              },
              monthly: {
                enabled: (formData.monthlyEmails?.length || 0) > 0,
                day: 1,
                time: "09:00",
                emails: ids(formData.monthlyEmails),
                phones: [],
              },
            },
          },
        };

        onSave(payload);
      } else if (!isValid) {
        Toast.fire({
          icon: "info",
          title: intl.formatMessage(
            {
              id: "error.saveReactiva",
              defaultMessage: "No se pudo guardar la alerta: {errors}",
            },
            { errors: Object.values(errors).join(", ") }
          ),
        });
      }
    });
  };

  return (
    <div className="reactiva-form">
      <h3 className="reactiva-title">Editar alerta: Exceso reactiva</h3>

      <label className="reactiva-label">Nombre de la alerta</label>
      <input
        type="text"
        className="reactiva-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Alerta exceso de reactiva"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      <label className="reactiva-label">Dispositivo</label>
      <Select
        options={devices}
        value={formData.device}
        onChange={(val) => handleChange("device", val)}
        placeholder="Selecciona un dispositivo"
      />
      {errors.device && <small className="error-text">{errors.device}</small>}

      <label className="reactiva-label">Estructura tarifaria</label>
      <input
        type="text"
        className="reactiva-input"
        value={formData.tariff || ""}
        readOnly
        placeholder={
          formData.device
            ? "Estructura tarifaria no disponible"
            : "Seleccione un dispositivo"
        }
      />
      {errors.tariff && <small className="error-text">{errors.tariff}</small>}

      <label className="reactiva-label">Períodos de aplicación</label>
      <p className="reactiva-note">
        Las bandas horarias se obtienen de la configuración del proveedor.
      </p>

      {intervalMinutes ? (
        <p className="reactiva-note">
          Intervalo de integración: {intervalMinutes} minutos
        </p>
      ) : null}

      {timebandInputs}

      {errors.periods && <small className="error-text">{errors.periods}</small>}

      <label className="reactiva-label">¿Activar?</label>
      <div className="reactiva-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>
        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      <h4 className="reactiva-subtitle">Notificaciones</h4>

      <div className="reactiva-inline">
        Tiempo real (email):
        <Select
          isMulti
          className="reactiva-select"
          options={emailOptions}
          value={formData.realtimeEmails}
          onChange={(val) => handleChange("realtimeEmails", val || [])}
          placeholder="Selecciona usuarios"
        />
      </div>

      <h4 className="reactiva-subtitle">Resúmenes</h4>

      <label>Informe diario</label>
      <Select
        isMulti
        className="reactiva-select"
        options={emailOptions}
        value={formData.dailyEmails}
        onChange={(val) => handleChange("dailyEmails", val || [])}
        placeholder="Selecciona usuarios"
      />

      <label>Informe semanal</label>
      <Select
        isMulti
        className="reactiva-select"
        options={emailOptions}
        value={formData.weeklyEmails}
        onChange={(val) => handleChange("weeklyEmails", val || [])}
        placeholder="Selecciona usuarios"
      />

      <label>Informe mensual</label>
      <Select
        isMulti
        className="reactiva-select"
        options={emailOptions}
        value={formData.monthlyEmails}
        onChange={(val) => handleChange("monthlyEmails", val || [])}
        placeholder="Selecciona usuarios"
      />

      <label className="reactiva-label">Idioma</label>
      <select
        className="reactiva-select"
        value={formData.language}
        onChange={(e) => handleChange("language", e.target.value)}
      >
        <option value="es">Español</option>
        <option value="en">Inglés</option>
      </select>

      <div className="reactiva-actions">
        <button
          className="reactiva-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>
        <button className="reactiva-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default ReactiveAlertFormEdit;